import { useApi } from '@classgo/app-kit';
import { createSchoolYearInputSchema, updateSchoolYearInputSchema } from '@classgo/shared';
import { Input, Select } from '@classgo/ui';

import { academicApi } from '../academic/api';
import type { SchoolYearView } from '../academic/types';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { FormShell } from './form-shell';

/**
 * Create or edit a school year (name, calendar, dates).
 */
export const YearForm = ({
  current,
  onDone,
}: {
  current: SchoolYearView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const year = new Date().getFullYear();
  const form = useForm({
    schema: current ? updateSchoolYearInputSchema : createSchoolYearInputSchema,
    initial: {
      name: current?.name ?? String(year + (new Date().getMonth() >= 6 ? 1 : 0)),
      calendar: current?.calendar ?? ('A' as 'A' | 'B'),
      startsOn: current?.startsOn ?? '',
      endsOn: current?.endsOn ?? '',
    },
    toInput: (values) => values,
  });
  const error = (key: 'name' | 'calendar' | 'startsOn' | 'endsOn') =>
    form.fieldError(key) ? { error: form.fieldError(key) } : {};
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('years', current.id, input);
            else await academicApi(api).create('years', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <Input
        label={t('years.field.name')}
        required
        value={form.values.name}
        onChange={(event) => form.set('name', event.target.value)}
        {...error('name')}
      />
      <Select
        label={t('years.field.calendar')}
        help={t('years.field.calendar.help')}
        value={form.values.calendar}
        onChange={(event) => form.set('calendar', event.target.value as 'A' | 'B')}
        options={[
          { value: 'A', label: t('years.calendar.A') },
          { value: 'B', label: t('years.calendar.B') },
        ]}
        {...error('calendar')}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('years.field.startsOn')}
          type="date"
          required
          value={form.values.startsOn}
          onChange={(event) => form.set('startsOn', event.target.value)}
          {...error('startsOn')}
        />
        <Input
          label={t('years.field.endsOn')}
          type="date"
          required
          value={form.values.endsOn}
          onChange={(event) => form.set('endsOn', event.target.value)}
          {...error('endsOn')}
        />
      </div>
    </FormShell>
  );
};
